import { useId } from "react";

/**
 * The average audience over the period, one point a slice (a day for a week),
 * drawn as a line over a soft area that fills its box. A slice without
 * measurements breaks the line: a gap is never drawn as zero.
 */
export function TrendLine({
  values,
  color,
  label,
}: {
  values: readonly (number | null)[];
  /** A CSS color: the line takes the trend's status, the area a faint wash of it. */
  color: string;
  label: string;
}) {
  const gradient = useId();
  const measured = values.filter((value): value is number => value !== null);
  if (values.length < 2 || measured.length < 2) return null;
  const top = Math.max(...measured);
  const low = Math.min(...measured);
  // The scale hugs the values so a change shows, with room below its lowest
  // point (half the range, never under zero): a steady audience still reads
  // as nearly flat, not as a swing.
  const floor = Math.max(0, low - Math.max(top - low, top * 0.1) * 0.5);
  const span = top - floor || 1;
  const x = (index: number) => (index / (values.length - 1)) * 100;
  // Room above and below, so the stroke never touches the edges.
  const y = (value: number) => 92 - ((value - floor) / span) * 82;
  // Runs of measured slices; a gap starts a new run.
  const segments: { index: number; value: number }[][] = [[]];
  values.forEach((value, index) => {
    const current = segments[segments.length - 1];
    if (value === null) {
      if (current.length) segments.push([]);
    } else current.push({ index, value });
  });
  const drawn = segments.filter((segment) => segment.length > 0);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="block h-full w-full overflow-visible"
    >
      <defs>
        <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {drawn.map((segment) => {
        const line = segment.map((point) => `${x(point.index)},${y(point.value)}`).join(" ");
        const first = x(segment[0].index);
        const last = x(segment[segment.length - 1].index);
        return (
          <g key={segment[0].index}>
            {segment.length > 1 ? (
              <polygon points={`${first},100 ${line} ${last},100`} fill={`url(#${gradient})`} />
            ) : null}
            <polyline
              points={segment.length > 1 ? line : `${line} ${line}`}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}
    </svg>
  );
}
